import React, {useEffect, useRef, useState} from "react";
import {Switch} from "@headlessui/react";
import PlaceAutocomplete from "@/customComponents/uiComponents/global/PlaceAutocomplete.jsx";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import TripRouteMap from "@/components/map/TripRouteMap.jsx";
import { motion, Reorder, useDragControls } from "framer-motion";
import Modal from "react-modal";
import {hopnLanguageArray} from "@/utils/functions.js";
import {tripService} from "@/services/tripService";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {queryClient} from "@/lib/queryClient";
import {POLICIES_KEY} from "@/hooks/usePolicies";
import {WhyUs} from "@/customComponents/BookRideSteps/Components/whyUs.jsx";
import {HopnPerks} from "@/customComponents/BookRideSteps/Components/hopnPerks.jsx";
import {HopnModal} from "@/customComponents/BookRideSteps/Components/hopnModal.jsx";
import {BookingModalShell, ModalFooter} from "@/customComponents/BookRideSteps/Components/BookingModalShell.jsx";
import {ReturnTripInfoContent} from "@/customComponents/BookRideSteps/Components/returnTripInfo.jsx";
import {primeIOSKeyboard, focusFromProxy, releaseIOSKeyboardProxy} from "@/utils/iosFocusProxy";
import {isIOS} from "@/utils/platform";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {isAirportTrip, isNearAirport} from "@/utils/airports";
import {NON_PERSISTED_STEPS, normalizeStep} from "@/constants/steps";
import {MODAL_CLOSE_MS, modalContainerClass, modalOverlayClass} from "@/utils/modalAnim.js";
import {
    closeIcon,
    dragHandle,
    dragLines,
    trashIcon,
    plusLight,
    personIcon,
    returnTripIcon,
    bigClose,
} from "@img/index.js";

const MOBILE_QUERY = '(max-width: 767px)';

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
    );
    useEffect(() => {
        const mq = window.matchMedia(MOBILE_QUERY);
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return isMobile;
}

// The OTHER ends of this trip, for PlaceAutocomplete's `excludeNear`. An airport already
// picked at one end (its pin, or one of its terminals) is then kept out of every other
// field's list — the same airport can't be both the pickup and the drop-off.
const otherLocations = (destinations, index) =>
    destinations
        .filter((_, i) => i !== index)
        .map((d) => ({lat: d.lat, lng: d.lng}));

// Each stopover row owns its own useDragControls so the drag handle is the
// only thing that can start a reorder (dragListener={false} on the Item).
function StopoverReorderItem({
    dest,
    index,
    destinations,
    errors,
    handlePlaceSelected,
    removeDestination,
    isMobile,
    // The mobile stopovers sheet renders over this page, so its fields need their own ids.
    idPrefix = "hopn-destination-",
}) {
    const controls = useDragControls();
    const showHandle = destinations.length >= 4;
    const handleStart = (e) => controls.start(e);

    const handleNode = (
        <span
            className="hopn-drag-handle"
            onPointerDown={handleStart}
            style={{cursor: 'grab', touchAction: 'none'}}
        >
            <img src={isMobile ? dragLines : dragHandle} alt="Drag to reorder" draggable="false"/>
        </span>
    );

    return (
        <Reorder.Item
            value={dest}
            as="div"
            className="hopn-stopover-item"
            dragListener={false}
            dragControls={controls}
        >
            <div
                className={`customer-destinations-area-wrapper ${
                    errors.destinations[dest.id] ? "error" : ""
                }`}
            >
                <div className="inner destination">
                    {!isMobile && (
                        <label
                            hopn-data-index={`additional_destination_label_${index - 1}`}
                            hopn-data-category="second_banner"
                        >
                            {showHandle && handleNode}
                            {hopnLanguageArray("second_banner", `additional_destination_label_${index - 1}`, `Additional Destination ${index - 1}`)}
                        </label>
                    )}
                    {isMobile && showHandle && handleNode}
                    <div className="add-location" style={{position: "relative"}}>
                        <PlaceAutocomplete
                            key={dest.address === "" ? `${dest.id}-empty` : `${dest.id}-filled`}
                            id={`${idPrefix}${index}`}
                            className="google-autocomplete-input"
                            placeholder={isMobile ? `Stopover ${index - 1}` : hopnLanguageArray("second_banner", `additional_destination_placeholder_${index - 1}`, `Enter additional destination ${index - 1}`)}
                            value={dest.address}
                            onPlaceSelected={(data) => handlePlaceSelected(index, data)}
                            excludeNear={otherLocations(destinations, index)}
                        />
                        {/* Mobile: the remove button sits inside the field as a trash icon. */}
                        {isMobile && (
                            <button
                                type="button"
                                className="inner-cross stopover-delete"
                                onClick={() => removeDestination(dest.id)}
                                hopn-data-index={`delete_destination_${index}`}
                                hopn-data-category="second_banner"
                            >
                                <img src={trashIcon} alt="Remove" style={{width: "16px", height: "16px"}}/>
                            </button>
                        )}
                    </div>
                </div>
                {!isMobile && (
                    <button
                        className="delete-destination"
                        onClick={() => removeDestination(dest.id)}
                        hopn-data-index={`delete_destination_${index}`}
                        hopn-data-category="second_banner"
                    >
                        <img src={closeIcon} alt="Remove" style={{width: "16px", height: "16px"}}/>
                    </button>
                )}
            </div>
            {errors.destinations[dest.id] && (
                <p className="error-text" style={{color: "red", fontSize: "12px"}}
                   hopn-data-index={`error_destination_${index}`}
                   hopn-data-category="second_banner">
                    {errors.destinations[dest.id]}
                </p>
            )}
        </Reorder.Item>
    );
}

function DestinationBanner() {
    const isMobile = useIsMobile();
    const modalRef = useRef(null);

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [mapUiError, setMapUiError] = useState(false);
    const [mapUiErrorMessage, setMapUiErrorMessage] = useState(false);
    const [recalculate, setRecalculate] = useState(false);
    const [recalculateError, setRecalculateError] = useState(false);
    const [whyUsPopup, setWhyUsPopup] = useState(storage.get(STORAGE_KEYS.HAS_USER_CLOSE_BANNER_POPUP) === true);
    const [pageRightSection, setPageRightSection] = useState(
        storage.get(STORAGE_KEYS.BANNER_VIEW_TYPE) || "instructions"
    );
    const [mapUi, setMapUi] = useState(storage.get(STORAGE_KEYS.MAP_UI, []));
    const [errors, setErrors] = useState({destinations: {}});
    const [hasUserClosePopup, setHasUserClosePopup] = useState(storage.get(STORAGE_KEYS.HAS_USER_CLOSE_POPUP) === true);
    const [returnBooking, setReturnBooking] = useState(() => storage.get(STORAGE_KEYS.RETURN_BOOKING) === true);
    const [returnTooltip, setReturnTooltip] = useState(false);
    const [returnInfoOpen, setReturnInfoOpen] = useState(false);   // mobile modal version of returnTooltip
    const [stopoversOpen, setStopoversOpen] = useState(false);      // mobile stopovers sheet
    const [doneRedirectTooltip, setDoneRedirectTooltip] = useState(false);
    const [destinations, setDestinations] = useState(() => {
        let stored = storage.get(STORAGE_KEYS.DESTINATIONS, []);
        if (stored.length === 1) {
            const emptySecond = {id: Date.now(), address: "", lat: "", lng: "", types: {}, city: "", state: "", country: "", postal_code: ""};
            stored = [...stored, emptySecond];
        }
        if (stored.length === 0) {
            stored = [
                {id: Date.now(), address: "", lat: "", lng: "", types: {}, city: "", state: "", country: "", postal_code: ""},
                {id: Date.now() + 1, address: "", lat: "", lng: "", types: {}, city: "", state: "", country: "", postal_code: ""},
            ];
        }
        storage.set(STORAGE_KEYS.DESTINATIONS, stored);
        return stored;
    });

    const [step, _setStep] = useState(() => storage.get(STORAGE_KEYS.LATEST_STEP) || "passengerQuantity");
    // Unified "previous step" breadcrumb (replaces POLICIES_ORIGIN + the manual
    // PREVIOUS_STEP stamps): auto-captured on every navigation (the outgoing step
    // becomes the next page's "previous") and mirrored to sessionStorage so a reload
    // restores the back target. Children read props.previousStep; optional 2nd arg overrides.
    const [previousStep, setPreviousStep] = useState(() => storage.get(STORAGE_KEYS.PREVIOUS_STEP) || null);
    const setStep = (nextStep, prevOverride) => {
        const prev = prevOverride ?? step;
        setPreviousStep(prev);
        storage.set(STORAGE_KEYS.PREVIOUS_STEP, prev);
        _setStep(nextStep);
    };

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({top: 0, behavior: "smooth"});
        }, 50);
        // Delayed focus → on iOS it only gets the keyboard because mainBanner primed a
        // proxy input when the pickup was picked (utils/iosFocusProxy); hand it over.
        // First empty location: pickup, else drop-off.
        const focusTarget = !destinations[0]?.address ? 'hopn-destination-0'
            : !destinations[1]?.address ? 'hopn-destination-1'
            : null;
        if (focusTarget) {
            setTimeout(() => focusFromProxy(focusTarget), 200);
        } else {
            releaseIOSKeyboardProxy();
        }
    }, []);

    useEffect(() => {
        if (!NON_PERSISTED_STEPS.includes(normalizeStep(step))) {
            storage.set(STORAGE_KEYS.LATEST_STEP, step);
        }
    }, [step]);

    useEffect(() => {
        const savedStep = storage.get(STORAGE_KEYS.LATEST_STEP);
        if (savedStep && hasUserClosePopup) {
            setStep(savedStep);
            setModalIsOpen(true);
        }
    }, [hasUserClosePopup]);

    // When validation fails, bring the first empty destination field into view so its
    // inline "required" error is visible (mirrors the booking portal's second banner).
    const scrollToFirstError = () => {
        const firstEmptyIndex = destinations.findIndex((d) => !d.address);
        if (firstEmptyIndex === -1) return;
        setTimeout(() => {
            const input = document.getElementById(`hopn-destination-${firstEmptyIndex}`);
            const target = input?.closest('.customer-destinations-area-wrapper') || input;
            target?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 60);
    };

    const openModal = () => {
        if (!validateDestinations()) {
            scrollToFirstError();
            return;
        }
        setModalIsOpen(true);
        setHasUserClosePopup(true);
        storage.set(STORAGE_KEYS.HAS_USER_CLOSE_POPUP, true);
    };
    const closeModal = () => {
        setModalIsOpen(false);
        setHasUserClosePopup(false);
        storage.set(STORAGE_KEYS.HAS_USER_CLOSE_POPUP, false);
    };

    const whyUsCloseModal = () => {
        setWhyUsPopup(false);
        storage.set(STORAGE_KEYS.HAS_USER_CLOSE_BANNER_POPUP, false);
    };

    const handlePlaceSelected = (index, data) => {
        const destId = destinations[index]?.id;
        setDestinations(prev =>
            prev.map((d, i) =>
                i === index
                    ? {...d, address: data.address, lat: data.lat, lng: data.lng, types: data.types, city: data.city, state: data.state, country: data.country, postal_code: data.postal_code}
                    : d
            )
        );
        setErrors(prev => ({
            ...prev,
            destinations: {...prev.destinations, [destId]: ""}
        }));
    };

    const addDestination = () => {
        const newDestination = {id: Date.now(), address: "", lat: "", lng: "", types: {}, city: "", state: "", country: "", postal_code: ""};
        const updated = [...destinations, newDestination];
        const newIndex = updated.length - 1;
        setDestinations(updated);
        storage.set(STORAGE_KEYS.DESTINATIONS, updated);
        const container = document.querySelector('.customer-destinations-area');
        // In the tap, so iOS raises the keyboard for the stopover field that mounts next.
        primeIOSKeyboard();
        setTimeout(() => {
            if (container) {
                container.scrollTo({top: container.scrollHeight, behavior: 'smooth'});
            }
            focusFromProxy(`hopn-destination-${newIndex}`);
        }, 100);
    };

    // Mobile: "Want to pick someone else?" manages stopovers in their own sheet. Opens with
    // one empty stopover field when there are none yet, so the user doesn't add the first one.
    const STOPOVER_ID_PREFIX = "hopn-stopover-";
    const emptyDestination = () => ({id: Date.now(), address: "", lat: "", lng: "", types: {}, city: "", state: "", country: "", postal_code: ""});
    const openStopovers = () => {
        primeIOSKeyboard();
        let updated = destinations;
        if (destinations.length <= 2) {
            updated = [...destinations, emptyDestination()];
            setDestinations(updated);
            storage.set(STORAGE_KEYS.DESTINATIONS, updated);
        }
        setStopoversOpen(true);
        const firstEmpty = updated.findIndex((d, i) => i >= 2 && !d.address);
        if (firstEmpty !== -1) setTimeout(() => focusFromProxy(`${STOPOVER_ID_PREFIX}${firstEmpty}`), 350);
        else releaseIOSKeyboardProxy();
    };
    const addStopover = () => {
        const updated = [...destinations, emptyDestination()];
        setDestinations(updated);
        storage.set(STORAGE_KEYS.DESTINATIONS, updated);
        primeIOSKeyboard();
        setTimeout(() => focusFromProxy(`${STOPOVER_ID_PREFIX}${updated.length - 1}`), 100);
    };
    // On close, drop stopovers left empty so they don't block the form's validation.
    const closeStopovers = () => {
        setStopoversOpen(false);
        const pruned = destinations.filter((d, i) => i < 2 || d.address);
        if (pruned.length !== destinations.length) {
            setDestinations(pruned);
            storage.set(STORAGE_KEYS.DESTINATIONS, pruned);
        }
    };

    const removeDestination = (id) => {
        const updated = destinations.filter((d) => d.id !== id);
        setDestinations(updated);
        storage.set(STORAGE_KEYS.DESTINATIONS, updated);
    };

    const handleStopoverReorder = (newStopoverOrder) => {
        const updated = [destinations[0], destinations[1], ...newStopoverOrder];
        setDestinations(updated);
        storage.set(STORAGE_KEYS.DESTINATIONS, updated);
    };

    const handleMapUi = async () => {
        const hasEmpty = destinations.some(dest => !dest.address);
        if (hasEmpty) return;

        setMapUi([]);
        setPageRightSection("map");

        const storedDests = storage.getDestinations
            ? storage.getDestinations()
            : storage.get(STORAGE_KEYS.DESTINATIONS, []);
        if (!Array.isArray(storedDests) || storedDests.length < 2) return;

        const origin = storedDests[0];
        const destination = storedDests[1];
        const stopOvers = storedDests.slice(2);

        const result = await tripService.calculateRoute(origin, destination, stopOvers);

        if (!result.success) {
            setMapUiErrorMessage(result.error || 'Failed to calculate route');
            setMapUiError(true);
            setMapUi([]);
            console.error(result.error);
            return;
        }

        const routeHash = result.data?.route_hash ?? result.data?.routeHash;
        const routeData = result.data?.route_data ?? result.data?.routeData;
        storage.set(STORAGE_KEYS.ROUTE_HASH, routeHash);
        storage.set(STORAGE_KEYS.ROUTE_BREAKDOWN, routeData);
        storage.set(STORAGE_KEYS.MAP_UI, routeData);
        setMapUi(routeData);
        storage.set(STORAGE_KEYS.BANNER_VIEW_TYPE, "map");
    };

    const validateDestinations = () => {
        let hasError = false;
        const newErrors = {destinations: {}};
        destinations.forEach((dest) => {
            if (!dest.address) {
                newErrors.destinations[dest.id] = "This field is required";
                hasError = true;
            }
        });
        setErrors(newErrors);
        return !hasError;
    };

    const prevDestinationsRef = useRef(JSON.stringify(storage.get(STORAGE_KEYS.DESTINATIONS, [])));
    useEffect(() => {
        const serialized = JSON.stringify(destinations);
        storage.set(STORAGE_KEYS.DESTINATIONS, destinations);

        setMapUiError(false);

        const destinationsChanged = prevDestinationsRef.current !== serialized;
        if (prevDestinationsRef.current && destinationsChanged) {
            // Location changed -> route/price is now stale. Empty the cached
            // calculate-cost response so carSelection re-hits the endpoint (with its
            // loading state) the same way a date/time change does.
            storage.remove(STORAGE_KEYS.ALL_CARS_API);
            // Policies (trip_direction + distance) are now stale too. Drop the
            // Infinity-cached response so the next time the user crosses the modal gate
            // it re-fetches for the new trip (fixes stale policies after a location edit).
            queryClient.removeQueries({queryKey: POLICIES_KEY});
            // Reset flight / smart-time details only when the change can invalidate the
            // flight-arrival suggestion: trip direction, the pickup (the flight's airport),
            // or the stopovers. A drop-off-only change keeps the flight (and the date/time).
            const prevDests = JSON.parse(prevDestinationsRef.current || "[]");
            const dirOf = (d) => {
                const p = d[0], dz = d[1];
                const from = p?.lat && p?.lng ? !!isNearAirport(p.lat, p.lng) : false;
                const to = dz?.lat && dz?.lng ? !!isNearAirport(dz.lat, dz.lng) : false;
                return from ? "from" : to ? "to" : "others";
            };
            if (
                dirOf(prevDests) !== dirOf(destinations) ||
                JSON.stringify(prevDests[0] ?? null) !== JSON.stringify(destinations[0] ?? null) ||
                JSON.stringify(prevDests.slice(2)) !== JSON.stringify(destinations.slice(2))
            ) {
                storage.remove(STORAGE_KEYS.FLIGHT_NUMBER);
                storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
                storage.remove(STORAGE_KEYS.SMART_TIME_SELECTED);
            }
        }
        prevDestinationsRef.current = serialized;

        const hasStart = destinations[0]?.lat && destinations[0]?.lng;
        const hasDestination = destinations[1]?.lat && destinations[1]?.lng;

        if (hasStart && hasDestination) {
            handleMapUi();
        } else {
            setMapUi([]);
            setPageRightSection("instructions");
            storage.remove(STORAGE_KEYS.MAP_UI);
            storage.remove(STORAGE_KEYS.ROUTE_HASH);
            storage.remove(STORAGE_KEYS.ROUTE_BREAKDOWN);
            storage.set(STORAGE_KEYS.BANNER_VIEW_TYPE, "instructions");
        }
    }, [destinations]);

    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true;
    const hasStart = destinations[0]?.lat && destinations[0]?.lng;
    const hasDestination = destinations[1]?.lat && destinations[1]?.lng;
    // Any blank field means the route can't be calculated; keep the "+ Add Passenger"
    // button ENABLED in that case so the click surfaces the validation error (and only
    // show the calculating spinner once every field is filled).
    const hasEmptyDestination = destinations.some((d) => !d.address);
    const isCalculatingRoute = hasStart && hasDestination && !hasEmptyDestination && !mapUi?.polyline && !mapUiError;

    const handleEditDone = async () => {
        if (!tripService.needsRecalculation()) {
            setStep('orderDetails');
            openModal();
            return;
        }
        if (isAirportTrip() && !doneRedirectTooltip) {
            setDoneRedirectTooltip(true);
            return;
        }
        setDoneRedirectTooltip(false);
        setRecalculate(true);
        const res = await tripService.recalculateCost();
        setRecalculate(false);
        if (res.success) {
            setStep(isAirportTrip() ? 'travelingDateTime' : 'orderDetails');
            openModal();
        } else {
            console.error(res);
            setRecalculateError(res.error);
        }
    };

    const renderClearButton = (destId) => (
        <button
            type="button"
            className="inner-cross"
            onClick={() => {
                const updated = destinations.map((d) =>
                    d.id === destId ? {...d, address: "", lat: "", lng: "", types: {}, city: "", state: "", country: "", postal_code: ""} : d
                );
                setDestinations(updated);
                storage.set(STORAGE_KEYS.DESTINATIONS, updated);
                // Put the cursor back in the cleared field (it remounts empty). Primed in
                // the tap so iOS raises the keyboard too.
                const idx = destinations.findIndex((d) => d.id === destId);
                primeIOSKeyboard();
                setTimeout(() => focusFromProxy(`hopn-destination-${idx}`), 50);
            }}
            hopn-data-index="clear_destination"
            hopn-data-category="second_banner"
        >
            <img src={closeIcon} alt="Clear" style={{width: "14px", height: "14px"}}/>
        </button>
    );

    return (
        <>
            {/* Mobile "Bundle your return trip" info — same content as the desktop tooltip. */}
            <Modal
                isOpen={returnInfoOpen}
                onRequestClose={() => setReturnInfoOpen(false)}
                closeTimeoutMS={MODAL_CLOSE_MS}
                overlayClassName={modalOverlayClass('hopn-modal-overlay--stacked')}
                className={modalContainerClass('step-returnTripInfo')}
                shouldCloseOnOverlayClick={false}
            >
                <BookingModalShell step="returnTripInfo" overlay={true}>
                    <ModalHeader
                        title=""
                        totalSteps={0}
                        cancelButton={true}
                        cancelButtonType="icon"
                        showBackButton={false}
                        closeModal={() => setReturnInfoOpen(false)}
                    />
                    <div className="modalPages-spacing return-trip-info">
                        <ReturnTripInfoContent/>
                    </div>
                </BookingModalShell>
            </Modal>

            {/* Mobile stopovers sheet, opened by "Want to pick someone else?". */}
            <Modal
                isOpen={stopoversOpen}
                onRequestClose={closeStopovers}
                closeTimeoutMS={MODAL_CLOSE_MS}
                overlayClassName={modalOverlayClass('hopn-modal-overlay--stacked')}
                className={modalContainerClass('step-stopovers')}
                shouldCloseOnOverlayClick={false}
            >
                <BookingModalShell step="stopovers" overlay={true}>
                    <ModalHeader
                        title="Stopovers"
                        totalSteps={0}
                        cancelButton={true}
                        cancelButtonType="icon"
                        showBackButton={false}
                        closeModal={closeStopovers}
                    />
                    <div className="modalPages-spacing stopovers-step">
                        <div className="customer-destinations-area">
                            <Reorder.Group axis="y" values={destinations.slice(2)} onReorder={handleStopoverReorder}
                                           as="div" className="hopn-stopovers-list">
                                {destinations.slice(2).map((dest, i) => (
                                    <StopoverReorderItem
                                        key={dest.id}
                                        dest={dest}
                                        index={i + 2}
                                        destinations={destinations}
                                        errors={{destinations: {}}}
                                        handlePlaceSelected={handlePlaceSelected}
                                        removeDestination={removeDestination}
                                        isMobile={true}
                                        idPrefix={STOPOVER_ID_PREFIX}
                                    />
                                ))}
                            </Reorder.Group>
                        </div>
                        <button type="button" className="stopovers-add-link" onClick={addStopover}>
                            + Add Stopover
                        </button>
                    </div>
                    <ModalFooter>
                        <button type="button" className="hopn-add-passenger-button no-icon" onClick={closeStopovers}>
                            Done
                        </button>
                    </ModalFooter>
                </BookingModalShell>
            </Modal>

            <Modal
                isOpen={whyUsPopup}
                onRequestClose={whyUsCloseModal}
                overlayClassName="hopn-modal-overlay"
                className="hopn-modal-container"
                shouldCloseOnOverlayClick={false}
            >
                <div className="whyUs">
                    <WhyUs setWhyUsPopup={setWhyUsPopup}/>
                </div>
            </Modal>

            <div className="hopn-banner-area space second">
                <div className="for-mobile"></div>

                <div className="hopn-banner-left">
                    {!isMobile && (
                        <h2 className="booking-locations-heading">
                            Where are you going? <span>Enter your pickup and drop-off location</span>
                        </h2>
                    )}

                    {isMobile && hasStart && hasDestination && (
                        <div className="booking-locations-map" hopn-data-category="second_banner">
                            {!mapUi?.polyline ? (
                                <div className="booking-locations-map-placeholder">
                                    {mapUiError
                                        ? <div className="message">{mapUiErrorMessage}</div>
                                        : <Loader/>
                                    }
                                </div>
                            ) : (
                                <TripRouteMap polyline={mapUi.polyline} legs={mapUi.legs} className="min-h-[300px] h-[300px]"/>
                            )}
                        </div>
                    )}

                    <div className="customer-destinations-area">
                        {destinations[0] && (
                            <React.Fragment key={destinations[0].id}>
                                <div className={`customer-destinations-area-wrapper ${errors.destinations[destinations[0].id] ? "error" : ""}`}>
                                    <div className="inner destination">
                                        {!isMobile && (
                                            <label hopn-data-index="start_label" hopn-data-category="second_banner">
                                                <b>Start</b> (Pickup location)
                                            </label>
                                        )}
                                        <div className="add-location" style={{position: "relative"}}>
                                            <PlaceAutocomplete
                                                key={destinations[0].address === "" ? `${destinations[0].id}-empty` : `${destinations[0].id}-filled`}
                                                id="hopn-destination-0"
                                                className="google-autocomplete-input"
                                                placeholder={isMobile ? "Start (Pickup location)" : hopnLanguageArray("second_banner", "start_placeholder", "Enter start location")}
                                                value={destinations[0].address}
                                                onPlaceSelected={(data) => handlePlaceSelected(0, data)}
                                                excludeNear={otherLocations(destinations, 0)}
                                                readOnly={!!destinations[0].address}
                                            />
                                            {destinations[0].address && renderClearButton(destinations[0].id)}
                                        </div>
                                    </div>
                                </div>
                                {errors.destinations[destinations[0].id] && (
                                    <p className="error-text" style={{color: "red", fontSize: "12px"}}
                                       hopn-data-index="error_destination_0" hopn-data-category="second_banner">
                                        {errors.destinations[destinations[0].id]}
                                    </p>
                                )}
                            </React.Fragment>
                        )}

                        {destinations.length > 2 && (
                            <Reorder.Group axis="y" values={destinations.slice(2)} onReorder={handleStopoverReorder}
                                           as="div" className="hopn-stopovers-list">
                                {destinations.slice(2).map((dest, i) => (
                                    <StopoverReorderItem
                                        key={dest.id}
                                        dest={dest}
                                        index={i + 2}
                                        destinations={destinations}
                                        errors={errors}
                                        handlePlaceSelected={handlePlaceSelected}
                                        removeDestination={removeDestination}
                                        isMobile={isMobile}
                                    />
                                ))}
                            </Reorder.Group>
                        )}

                        {destinations[1] && (
                            <React.Fragment key={destinations[1].id}>
                                <div className={`customer-destinations-area-wrapper ${errors.destinations[destinations[1].id] ? "error" : ""}`}>
                                    <div className="inner destination">
                                        {!isMobile && (
                                            <label hopn-data-index="destination_label" hopn-data-category="second_banner">
                                                <b>Destination</b> (Drop-off location)
                                            </label>
                                        )}
                                        <div className="add-location" style={{position: "relative"}}>
                                            <PlaceAutocomplete
                                                key={destinations[1].address === "" ? `${destinations[1].id}-empty` : `${destinations[1].id}-filled`}
                                                id="hopn-destination-1"
                                                className="google-autocomplete-input"
                                                placeholder={isMobile ? "Destination (Drop-off location)" : hopnLanguageArray("second_banner", "destination_placeholder", "Enter destination")}
                                                value={destinations[1].address}
                                                onPlaceSelected={(data) => handlePlaceSelected(1, data)}
                                                excludeNear={otherLocations(destinations, 1)}
                                                readOnly={!!destinations[1].address}
                                            />
                                            {destinations[1].address && renderClearButton(destinations[1].id)}
                                        </div>
                                    </div>
                                </div>
                                {errors.destinations[destinations[1].id] && (
                                    <p className="error-text" style={{color: "red", fontSize: "12px"}}
                                       hopn-data-index="error_destination_1" hopn-data-category="second_banner">
                                        {errors.destinations[destinations[1].id]}
                                    </p>
                                )}
                            </React.Fragment>
                        )}
                    </div>

                    <div className="trip_creation_extras">
                        <div className="hopn-return-booking-check">
                            <button
                                className="add-description"
                                onClick={() => {
                                    // Mobile: manage stopovers in their own sheet.
                                    if (window.innerWidth < 576) { openStopovers(); return; }
                                    addDestination();
                                }}
                                hopn-data-index="add_another_passenger"
                                hopn-data-category="second_banner"
                            >
                                <div className="hopn-return-booking-check-inner">
                                    <div className="info-icon"><img src={personIcon} alt=""/></div>
                                    Want to pick someone else?
                                </div>
                                <span className="add-someone">
                                    <img src={plusLight} alt="Plus"/>
                                </span>
                            </button>
                        </div>
                        <div className="hopn-return-booking-check">
                            <div className="hopn-return-booking-check-inner">
                                <div className="info-icon"><img src={returnTripIcon} alt=""/></div>
                                Bundle your return trip
                                <span className="save-text save-text-inline">Save 25%</span>
                            </div>
                            <div className="hopn-return-toggle-wrap">
                                <span className="save-text save-text-stacked">Save 25%</span>
                                <Switch
                                    checked={returnBooking}
                                    onChange={(newVal) => {
                                        setReturnBooking(newVal);
                                        storage.set(STORAGE_KEYS.RETURN_BOOKING, newVal);
                                        // Mobile: the info opens as a modal instead of the inline tooltip.
                                        if (newVal && window.innerWidth < 576) {
                                            setReturnInfoOpen(true);
                                            return;
                                        }
                                        setReturnTooltip(newVal);
                                    }}
                                    id="return-booking-check"
                                    className="hopn-switch"
                                >
                                    <span className="hopn-switch-thumb"/>
                                </Switch>
                            </div>
                            {returnTooltip && (
                                <div className="hopn-submit-confirm-tooltip left">
                                    <div className="tooltip-close-row">
                                        <button type="button" className="tooltip-close-btn" onClick={() => setReturnTooltip(false)} aria-label="Close">
                                            <img src={bigClose} alt="Close"/>
                                        </button>
                                    </div>
                                    <ReturnTripInfoContent/>
                                </div>
                            )}
                        </div>
                    </div>

                    {isEditMode ? (
                        <>
                            {recalculateError !== false && (<div className="error-message">{recalculateError}</div>)}
                            <div className="hopn-submit-btn-wrapper">
                                <button
                                    className="hopn-add-passenger-button no-icon"
                                    disabled={recalculate || !hasStart || !hasDestination || !(mapUi?.polyline)}
                                    onClick={handleEditDone}
                                >
                                    {recalculate || isCalculatingRoute
                                        ? <Loader color="#fff"/>
                                        : "Done"}
                                </button>
                                {doneRedirectTooltip && (
                                    <div className="hopn-submit-tooltip visible">
                                        <div className="tooltip-close-row">
                                            <button type="button" className="tooltip-close-btn" onClick={() => setDoneRedirectTooltip(false)} aria-label="Close">
                                                <img src={bigClose} alt="Close"/>
                                            </button>
                                        </div>
                                        <p>You'll be redirected to Date & Time so you can adjust it according to your new locations.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <button
                            className="hopn-add-passenger-button"
                            disabled={!hasEmptyDestination && !(mapUi?.polyline)}
                            onClick={openModal}
                        >
                            {isCalculatingRoute
                                ? <Loader color="#fff"/>
                                : "+ Add Passenger"}
                        </button>
                    )}

                    {/* The step class goes on the OVERLAY as well as the container:
                        booking-received needs the backdrop's own alignment changed (the
                        overlay is the flex parent), which no container-scoped rule reaches. */}
                    <Modal
                        isOpen={modalIsOpen}
                        onRequestClose={closeModal}
                        closeTimeoutMS={MODAL_CLOSE_MS}
                        overlayClassName={modalOverlayClass(`step-${step}`)}
                        className={modalContainerClass(`step-${step}`)}
                        shouldCloseOnOverlayClick={false}
                        // iOS: react-modal focuses its container on open, which pulls focus off the
                        // keyboard proxy (utils/iosFocusProxy) and closes the keyboard before the step's
                        // field can take it. Off iOS keep the default (a11y focus into the dialog).
                        shouldFocusAfterRender={!isIOS()}
                    >
                        <div ref={modalRef}>
                            <BookingModalShell step={step}>
                                {/* Step router. The entrance fade is CSS on `.hopn-step-fade` (styles.scss, inside
                                    the .hopn-modal-container block), NOT framer-motion. This used to be an
                                    AnimatePresence with mode="wait": the outgoing page had to finish a 150ms fade to
                                    opacity 0 BEFORE the next could mount, leaving the panel empty in between, and
                                    animating opacity through framer made Chromium promote a compositing layer when
                                    the animation started and drop it when it ended — re-rastering the modal and the
                                    page behind it. Together those read as the modal blinking instead of fading.

                                    The overlay and the container are now pinned to their own layers (see styles.scss)
                                    so that churn has nothing to take with it, and `key={step}` both remounts the step
                                    page and restarts the CSS animation, since a fresh element animates once on mount. */}
                                <div key={step} className="hopn-step-fade">
                                    <HopnModal
                                        step={step}
                                        setStep={setStep}
                                        previousStep={previousStep}
                                        closeModal={closeModal}
                                        airportCase={false}
                                    />
                                </div>
                            </BookingModalShell>
                        </div>
                    </Modal>
                </div>

                {!isMobile && (
                    <div className="hopn-banner-right">
                        {pageRightSection === "instructions" ? (
                            <div className="hopn-banner-right-inner">
                                <HopnPerks key="1" linkOnly onMorePerksClick={() => {
                                    setWhyUsPopup(true);
                                    storage.set(STORAGE_KEYS.HAS_USER_CLOSE_BANNER_POPUP, true);
                                }}/>
                            </div>
                        ) : (
                            <div className="banner-map" hopn-data-category="second_banner">
                                {!mapUi?.polyline ? (
                                    <>
                                        {mapUiError
                                            ? <div className="message">{mapUiErrorMessage}</div>
                                            : <Loader/>}
                                    </>
                                ) : (
                                    <TripRouteMap key="2" polyline={mapUi.polyline} legs={mapUi.legs} className="min-h-[660px] h-auto! self-stretch"/>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {isMobile && (
                    <div className="hopn-banner-right">
                        <div className="for-mobile">
                            <div className="hopn-banner-right-inner" hopn-data-category="second_banner">
                                <HopnPerks key="2" linkOnly onMorePerksClick={() => setWhyUsPopup(true)}/>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default DestinationBanner;
